import React, { useMemo } from 'react'
import cn from 'classnames'
import { plainToInstance } from 'class-transformer'

import { SelectButton } from '@components/SelectButton/SelectButton'
import { Button } from '@components/Buttons'
import { StoryCard } from '@components/StoryCard'
import { AdvanceIcon } from '@components/icons/AdvanceIcon'
import SelectPerPage from '@components/Pagination/components/SelectPerPage/SelectPerPage'
import { Pagination } from '@components/Pagination'
import { SearchRecommendType } from '@models/recommend/SearchRecommendType'
import { OptionType } from '@interfaces/OptionType'
import StoryCardSkeleton from '@components/StoryCardSkeleton'
import NoData from '@components/NoData'
import { useDrawer } from '@hooks/contexts/DrawerContext/DrawerContext'
import { StoryDetailReaderType } from '@models/story/StoryDetailReaderType'
import { PrimaryTypeEnum } from '@interfaces/PrimaryTypeEnum'
import { CategoryListProps } from './interface'
import OptionForm from '../OptionForm'

const perPageOptions = [
  {
    label: '10',
    value: 10,
  },
  {
    label: '20',
    value: 20,
  },
  {
    label: '30',
    value: 30,
  },
]

const defaultSearchRecommendationOptions = [
  { value: PrimaryTypeEnum.TOP_CHART, text: 'ท็อปชาร์ต' },
  { value: PrimaryTypeEnum.NEW, text: 'มาใหม่' },
  { value: PrimaryTypeEnum.HOT_NEW, text: 'ฮิตติดเทรนด์' },
  { value: PrimaryTypeEnum.ENDED, text: 'จบแล้ว' },
]

const CategoryList = ({
  recommendationSearch,
  showOption,
  perpage,
  page,
  total,
  recommendBooks,
  isEbook,
  initialValues,
  isLoading,
  isLoadingToggleBookLike,
  handleRecommendationSearchChange,
  toggleOption,
  handleSearch,
  perpageChange,
  handleLikeBook,
  handlePageChange,
}: CategoryListProps) => {
  const storyDrawer = useDrawer({ drawer: 'story' })
  const searchRecommendationOptions: OptionType[] = useMemo(() => {
    let options = defaultSearchRecommendationOptions
    if (isEbook) {
      options = defaultSearchRecommendationOptions.filter(
        opt => !opt.value.includes(PrimaryTypeEnum.ENDED)
      )
    }
    return options
  }, [isEbook])

  return (
    <div
      id='category-list'
      className='my-[50px] tablet-mobile:my-[30px] tablet-mobile:w-full'
    >
      <div className='flex items-center border-b-[1px] mobile:flex-col mobile:items-start border-gray-3 dark:border-purple mobile:border-none tablet-mobile:mx-5 pb-5 mb-5 mobile:mb-0'>
        <div className='flex-1 mobile:w-full mobile:overflow-x-auto'>
          <SelectButton
            options={searchRecommendationOptions}
            value={recommendationSearch}
            onChange={handleRecommendationSearchChange}
          />
        </div>
        <div className='mobile:w-full'>
          <div className='h-[1px] w-full border-b-[1px] my-5 border-gray-3 dark:border-purple desktop-tablet:hidden' />
          <Button
            type='button'
            onClick={toggleOption}
            className={cn('border mobile:w-full', {
              'bg-secondary dark:bg-purple text-white dark:text-white-2 dark:border-purple':
                !showOption,
              'bg-white dark:bg-dark-7 text-sencondary dark:text-dark-10 dark:border-dark-10':
                showOption,
            })}
            icon={
              <AdvanceIcon
                className={cn({
                  'text-white dark:text-white-2': !showOption,
                  'text-secondary dark:text-dark-10': showOption,
                })}
              />
            }
          >
            <span className='text-[14px] font-medium'>การค้นหาขั้นสูง</span>
          </Button>
        </div>
      </div>
      {showOption && (
        <div className='mobile:px-[21px]'>
          <OptionForm
            handleSearch={handleSearch}
            isEbook={isEbook}
            recommendationSearch={recommendationSearch}
            initialValues={initialValues}
          />
        </div>
      )}
      <>
        <div className='flex items-center tablet-mobile:px-[20px] mb-5'>
          <div className='flex-1 text-secondary-1 text-sm font-light'>
            <span className='mobile:hidden'>จำนวนงานเขียน:</span>
            <span className='text-secondary dark:text-dark-10 font-bold'>{` ${total.toLocaleString()} `}</span>
            รายการ
          </div>
          <div className='flex justify-end'>
            <SelectPerPage
              value={perpage}
              options={perPageOptions}
              handleSelect={perpageChange}
            />
          </div>
        </div>
        {isLoading ? (
          <section className='grid grid-cols-2 tablet:grid-cols-4 gap-5 mb-[30px]'>
            {[...Array(2)].map((l, i) => (
              <StoryCardSkeleton key={i} direction='horizontal' />
            ))}
          </section>
        ) : recommendBooks?.length ? (
          <>
            <section className='grid grid-cols-2 tablet:grid-cols-4 gap-[20px] mb-[30px] mobile:gap-[10px] tablet-mobile:px-[20px]'>
              {recommendBooks.map((story: SearchRecommendType) => (
                <StoryCard
                  key={story.id}
                  id={story.id}
                  title={story.title}
                  isShowBookPromotion={story.hasSalePromotions}
                  buyAllChaptersPromotion={story.buyAllChaptersPromotion}
                  bookChapterCampaignHeader={story.bookChapterCampaignHeader}
                  readFreePromotion={story.readFreePromotion}
                  bookBadges={story.bookBadges}
                  imageUrl={story.coverResizeImgPath || story.coverImgPath}
                  penName={story.penName?.firstPenName}
                  slug={story.penName?.user?.profilePageSlug}
                  content={story.cover}
                  category={story.category?.name}
                  tags={story.tags}
                  chapter={story.chapterCount}
                  view={story.viewCount}
                  chat={story.commentCount}
                  like={story.likeCount}
                  isLiked={story.isLiked}
                  isEnded={story.isEnded}
                  bookType={story.bookType}
                  isToggleBookLike={isLoadingToggleBookLike}
                  handleLikeBook={handleLikeBook}
                  categoryId={story.category?.id}
                  handleClickStoryCard={() => {
                    storyDrawer.show({
                      bookId: story.id,
                      initialData: plainToInstance(StoryDetailReaderType, {
                        id: story.id,
                        coverImgPath: story.coverImgPath,
                        title: story.title,
                        writingType: story.writingType,
                        publishedAt: story.publishedAt,
                        cover: story.cover,
                        viewCount: story.viewCount,
                        commentCount: story.commentCount,
                        likeCount: story.likeCount,
                        publishedChapterCount: story.chapterCount,
                        penName: story.penName,
                        category: story.category,
                        bookType: story.bookType,
                        tags: story.tags,
                        rating: story.rating,
                        isEnded: story.isEnded,
                        isEpub: story.isEpub,
                        isPdf: story.isPdf,
                        isLiked: story.isLiked,
                      }),
                    })
                  }}
                />
              ))}
            </section>
            <Pagination
              page={page}
              perpage={perpage}
              total={total}
              pageChange={handlePageChange}
            />
          </>
        ) : (
          <NoData />
        )}
      </>
    </div>
  )
}

export default CategoryList
